import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { get, patch } from '@/shared/api/api-client';
import type {
  MarkReferralBookedRequest,
  ReferralPage,
  ReferralRow,
  ReferralsParams,
} from '@/types/referral-api';

/**
 * Front-desk referral worklist (PROD-1395). Key factory and hooks follow src/cohorts/services/use-cohorts.ts.
 * GET api/Patients/referrals?status=sent&status=accepted&performerRole=dietitian&page=1&pageSize=25
 * PATCH api/Patients/{patientId}/referrals/{id}/status  { status, appointmentId }
 */

/** Patient's referral worklist. Under /api/Patients so the dev gateway routes it (Patient #1187). */
export const REFERRALS_LIST_PATH = '/api/Patients/referrals';

export const REFERRALS_KEY = ['referrals'] as const;
export const referralsListKey = (p: ReferralsParams) => [...REFERRALS_KEY, 'list', p] as const;

/** status is repeated (status=sent&status=accepted); unset values are skipped. */
export function buildReferralsQuery(p: ReferralsParams): string {
  const qs = new URLSearchParams();
  for (const status of p.statuses) qs.append('status', status);
  if (p.performerRole) qs.append('performerRole', p.performerRole);
  qs.append('page', String(p.page));
  qs.append('pageSize', String(p.pageSize));
  return `?${qs.toString()}`;
}

export function useReferrals(params: ReferralsParams) {
  return useQuery({
    queryKey: referralsListKey(params),
    queryFn: ({ signal }) => get<ReferralPage>(`${REFERRALS_LIST_PATH}${buildReferralsQuery(params)}`, { signal }),
    // The old page stays on screen while the next one loads, so the pager does not jump.
    placeholderData: keepPreviousData,
  });
}

/**
 * Marks a referral booked and links the appointment. 409 = illegal move or a concurrent change,
 * 404 = not found. The caller shows the failure, so the global toast stays off (`handledInline`).
 */
export function useMarkReferralBooked() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, referralId, appointmentId }: MarkReferralBookedRequest) =>
      patch<ReferralRow>(`/api/Patients/${patientId}/referrals/${referralId}/status`, {
        status: 'booked',
        appointmentId,
      }),
    meta: { handledInline: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: REFERRALS_KEY }),
  });
}
